import { DateTime } from 'luxon';

export type DateTimeRange<T = any> = T & {
  started_at: DateTime;
  ended_at: DateTime;
};

/**
 * Mutates the range of timestamps by either adding or removing a specified range.
 * Similar to `mutateTimestampRange` from Timely core, but generic with Luxon formats.
 *
 * @param action - 'add' or 'remove'
 * @param rangeMutation - The range(s) to be added or removed.
 * @param existingRanges - The array of existing timestamp ranges.
 * @returns An array of modified timestamp ranges.
 */
export function mutateDateTimeRange<T extends { started_at: DateTime; ended_at: DateTime }>(
  action: 'add' | 'remove',
  rangeMutation: DateTimeRange<T> | DateTimeRange<T>[],
  existingRanges: DateTimeRange<T>[]
): DateTimeRange<T>[] {
  const rangeMutations = Array.isArray(rangeMutation) ? rangeMutation : [rangeMutation];

  // We'll round the timestamps to full minutes to avoid any <1m Memories and _tiny_ selections
  // This makes it technically less correct, but nobody cares about per-second accuracy.
  const sortedRangeMutations = rangeMutations
    .map(range => {
      const started_at = range.started_at.startOf('minute');
      const ended_at = range.ended_at;
      const finalTo =
        ended_at.second === 0 && ended_at.diff(started_at, 'minutes').minutes >= 1
          ? ended_at
          : ended_at.plus({ minute: 1 }).startOf('minute');
      return { ...range, started_at, ended_at: finalTo };
    })
    .sort((a, b) => a.started_at.toMillis() - b.started_at.toMillis());

  const sortedExistingRanges = [...existingRanges].sort(
    (a, b) => a.started_at.toMillis() - b.started_at.toMillis()
  );

  switch (action) {
    case 'add':
      return sortedRangeMutations.reduce(
        (acc, range) => addRangeToExisting(range, acc),
        sortedExistingRanges
      );
    case 'remove':
      return sortedRangeMutations.reduce(
        (acc, range) => removeRangeFromExisting(range, acc),
        sortedExistingRanges
      );
    default:
      throw new Error('Invalid action');
  }
}

function addRangeToExisting<T extends { started_at: DateTime; ended_at: DateTime }>(
  rangeToAdd: DateTimeRange<T>,
  existingRanges: DateTimeRange<T>[]
): DateTimeRange<T>[] {
  const combinedRanges: DateTimeRange<T>[] = [];
  let expandedFrom = rangeToAdd.started_at;
  let expandedTo = rangeToAdd.ended_at;

  existingRanges.forEach(existingRange => {
    const existingFrom = existingRange.started_at;
    const existingTo = existingRange.ended_at;

    // Merge with existing range if overlapping
    if (existingTo >= expandedFrom && existingFrom <= expandedTo) {
      expandedFrom = DateTime.min(expandedFrom, existingFrom);
      expandedTo = DateTime.max(expandedTo, existingTo);
    } else {
      combinedRanges.push(existingRange);
    }
  });

  // Add the expanded or original range
  combinedRanges.push({ ...rangeToAdd, started_at: expandedFrom, ended_at: expandedTo });

  // Sort by start time for consistency
  return combinedRanges.sort((a, b) => a.started_at.toMillis() - b.started_at.toMillis());
}

function removeRangeFromExisting<T extends { started_at: DateTime; ended_at: DateTime }>(
  rangeToRemove: DateTimeRange<T>,
  existingRanges: DateTimeRange<T>[]
): DateTimeRange<T>[] {
  return existingRanges.reduce((adjustedRanges, currentRange) => {
    const currentFrom = currentRange.started_at;
    const currentTo = currentRange.ended_at;
    const removeFrom = rangeToRemove.started_at;
    const removeTo = rangeToRemove.ended_at;

    // Check and adjust overlapping ranges
    if (currentTo > removeFrom && currentFrom < removeTo) {
      if (currentFrom < removeFrom) {
        adjustedRanges.push({ ...currentRange, started_at: currentFrom, ended_at: removeFrom });
      }
      if (currentTo > removeTo) {
        adjustedRanges.push({ ...currentRange, started_at: removeTo, ended_at: currentTo });
      }
    } else {
      adjustedRanges.push(currentRange);
    }

    return adjustedRanges;
  }, [] as DateTimeRange<T>[]);
}
